/**
 * Activer une carte cadeau (`/cadeau/$code`, route PUBLIQUE, migration 0326) :
 * on voit ce qui est offert et par qui ; connecté, « Activer mon cadeau »
 * ajoute les SKILLCARDS au deck. Sans compte : connexion / création sur place.
 * Dans l'app native, l'activation se fait sur le site (App Store 3.1.1).
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Gift } from 'lucide-react';
import { Button, Card, CardDescription, CardTitle, MathText } from '@jeraisonne/ui';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { useAuth } from '@/lib/auth';
import { isNativeApp } from '@/lib/platform';
import {
  formatGiftCode,
  giftLotLabel,
  giftRedeemErrorMessage,
  giftUrl,
  normalizeGiftCode,
  redeemGift,
  usePublicGift,
} from '@/lib/skillcards/gifts';

export function RedeemGiftPage({ code: raw }: { code: string }) {
  const code = normalizeGiftCode(raw);
  const { session } = useAuth();
  const qc = useQueryClient();
  const gift = usePublicGift(code);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ cardId: string | null } | null>(null);
  const g = gift.data;

  async function activate() {
    if (!code) return;
    setBusy(true);
    setError(null);
    try {
      const r = await redeemGift(code);
      setDone({ cardId: r.card_id });
      void qc.invalidateQueries({ queryKey: ['student-skillcards'] });
      void qc.invalidateQueries({ queryKey: ['skillcard-gift', code] });
    } catch (e) {
      setError(giftRedeemErrorMessage((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-8">
      <div className="mb-5 text-center">
        <span className="bg-accent-500 mx-auto flex h-16 w-16 items-center justify-center rounded-2xl text-white">
          <Gift className="h-8 w-8" aria-hidden />
        </span>
        <h1 className="font-display text-ink-900 mt-3 text-2xl font-extrabold">
          Un cadeau pour toi !
        </h1>
        {code && <p className="text-ink-500 mt-1 tracking-widest">{formatGiftCode(code)}</p>}
      </div>

      <Card className="border-ink-100/60 space-y-4 p-5 shadow-lg">
        {!code || g?.status === 'not_found' ? (
          <p role="alert" className="text-error-600 text-sm">
            {giftRedeemErrorMessage('not_found')}
          </p>
        ) : gift.isLoading || !g ? (
          <div className="bg-ink-100 h-20 animate-pulse rounded-xl" />
        ) : (
          <>
            <p className="text-ink-800 text-center">
              {g.from_first_name ? `${g.from_first_name} t’offre ` : 'On t’offre '}
              <strong>
                <MathText>
                  {giftLotLabel(g.kind ?? 'card', g.label ?? '', g.card_count ?? 1)}
                </MathText>
              </strong>{' '}
              sur JeRaisonne.
            </p>

            {done || g.mine ? (
              <div className="space-y-3 text-center">
                <p className="font-semibold text-green-700">
                  C’est dans ton deck ! Tes SKILLCARDS sont actives pendant un an.
                </p>
                <Link
                  to={done?.cardId || g.card_id ? '/card/$cardId' : '/'}
                  params={{ cardId: (done?.cardId ?? g.card_id) || '' }}
                  className="bg-primary-600 inline-flex min-h-[2.75rem] w-full items-center justify-center rounded-xl px-5 text-sm font-semibold text-white active:scale-[0.98]"
                >
                  Découvrir mes SKILLCARDS
                </Link>
              </div>
            ) : g.status === 'redeemed' ? (
              <p className="text-ink-600 text-center text-sm">
                {giftRedeemErrorMessage('already_redeemed')}
              </p>
            ) : isNativeApp() ? (
              <p className="text-ink-600 text-center text-sm">
                Pour activer ce cadeau, ouvre ce lien dans ton navigateur :{' '}
                <span className="break-all font-semibold">{giftUrl(code)}</span>
              </p>
            ) : !session ? (
              <OtpAuthForm
                initialMode="signup"
                renderHeader={() => (
                  <>
                    <CardTitle>Connecte-toi pour l’activer</CardTitle>
                    <CardDescription>
                      Crée ton compte ou connecte-toi : le cadeau s’active ensuite en un clic.
                    </CardDescription>
                  </>
                )}
              />
            ) : (
              <Button fullWidth size="lg" loading={busy} onClick={() => void activate()}>
                Activer mon cadeau
              </Button>
            )}
            {error && (
              <p role="alert" className="text-error-600 text-center text-sm">
                {error}
              </p>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

/** Saisie d'un code cadeau (« Mes SKILLCARDS ») → page d'activation. */
export function GiftCodeForm({ onSubmitCode }: { onSubmitCode: (code: string) => void }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        const c = normalizeGiftCode(value);
        if (!c) {
          setError('Le code fait 8 caractères (lettres et chiffres).');
          return;
        }
        setError(null);
        onSubmitCode(c);
      }}
    >
      <label className="sr-only" htmlFor="gift-code">
        Code cadeau
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          id="gift-code"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Code cadeau (ex. ABCD-EFGH)"
          autoCapitalize="characters"
          className="border-ink-200 text-ink-900 h-11 w-full rounded-xl border bg-white px-4 text-sm uppercase tracking-widest focus:outline-none"
        />
        <Button type="submit" className="sm:w-auto" fullWidth>
          Activer
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}
    </form>
  );
}
